import React, {
  Fragment, FunctionComponent, useEffect, useRef, useState,
} from 'react';
import { connect, ConnectedProps } from 'react-redux';
import Form, { ChangedField } from 'shared/form';
import {
  TargetModel, TargetModelAccessFunction, TargetModelAccessFunctions, TargetModelAfCCC, TargetModelAfCCCSettings,
} from 'data/target/target.types';
import { CaseModel } from 'data/case/case.types';
import {
  FormTemplate, FormTemplateField, FormTemplateOption, FormTemplateSection,
} from 'data/template/template.types';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { CONFIG_FORM_MODE } from 'shared/popup/config-form-popup';
import './utimaco-lims-target-ccc.scss';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import { getSelectedDeliveryFunctionId } from 'global/global-selectors';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/accessFunction/accessFunction.actions';
import { KeyValuePair } from 'data/types';
import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import * as selectors from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.selectors';
import { getTargetDeliveryTemplateAccessFunctionList } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.actions';
import { TargetDeliveryTemplateAccessFunction } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.types';
import { getTemplateOptions } from 'data/targetDeliveryTemplateAccessFunction/targetDeliveryTemplateAccessFunction.adapter';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import { TargetInfoHashItem } from '../target-info-defs';
import { prepareOptions, getTargetTemplateValues } from './utils';
import utimacoTemplate from './UtimacoLIMSTargetCCCTemplate';

export interface GeneralUtimaco {
  targetDeliveryTemplate: string
}

export interface MCFlags {
  connectedLinePresentation? : boolean
  separateCallContentChannel? : boolean
  suppressInterceptNumber? : boolean
}

export interface TargetInfoUtimaco {
  LIID?: string
  DIR?: string
  TON?: string
}

export interface ICDUtimaco {
  LEA?: string
  CLASS?: string
  FILEREF?: string
}

export interface NetworkTypeUtimaco {
  IA?: boolean
  IMS?: boolean
  POC?: boolean
  NGN?: boolean
  VOIP?: boolean
}

export interface DataTypeUtimaco {
  DATA?: boolean
  IA?: boolean
  MM?: boolean
}

export interface MonitoringCenterTypeUtimaco {
  CCCDATAID?: string,
  CCCIAID?: string,
  CCCMMID?: string
}

const mapState = (state: RootState) => ({
  targetTemplates: selectors.getTargetDeliveryTemplateAccessFunctionList(state),
  deliveryFunctionId: getSelectedDeliveryFunctionId(state),
  accessFunction: accessFunctionSelectors.getAccessFunction(state)
});

const mapDispatch = {
  getAccessFunctionWithViewOptions: actions.getAccessFunctionWithViewOptions,
  getTargetDeliveryTemplateAccessFunctionList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

type UtimacoTargetCCCProps = PropsFromRedux & {
  handleUpdate: (afSettings: TargetModelAfCCCSettings[]) => TargetModel[];
  handleTargetInfoHashItemUpdate: (targetInfoHashItem : TargetInfoHashItem) => TargetModel[];
  handleSubmit: (targets: TargetModel[]) => void;
  targetInfoHashItem: TargetInfoHashItem | null;
  afDetails: TargetModelAccessFunction;
  afid: string;
  currentStateMode: any;
  targetId: string;
  caseId: string;
  targetType: string;
  selectedAfs: { afs: TargetModelAccessFunctions }
  afDIDetails: any;
  warrantCase: CaseModel;
  selectedTargetAf: any;
}

function getAfCCCInfo(afccc: TargetModelAfCCC[], afDetailsType?: string): TargetModelAfCCC | undefined {
  return afccc?.find((af: any) => af.afType === afDetailsType);
}

function adaptTargetCCCForm(
  warrantCase: CaseModel,
  accessFunction: AccessFunction | null,
  targetTemplates: TargetDeliveryTemplateAccessFunction[],
  afccc?: TargetModelAfCCC[],
  afDetailsType?: string,
  afId?: string,
  currentStateMode?:CONFIG_FORM_MODE,
  selectedAfs?: { afs: TargetModelAccessFunctions, actualPrimaryType?: string },
  afid?: string,
  targetId?:string,
):FormTemplate {
  const clonedTemplate = cloneDeep(utimacoTemplate);
  const primaryTarget = selectedAfs?.actualPrimaryType;

  const templateOptions = getTemplateOptions(targetTemplates || [], afDetailsType || '');

  (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.options = [
    { value: '', label: 'Select a template' },
    ...templateOptions,
  ];

  const leaOptions = getLeaOptions(accessFunction as AccessFunction);

  (clonedTemplate.ICD as FormTemplateSection).fields.LEA.options = leaOptions;
  (clonedTemplate.ICD as FormTemplateSection).fields.LEA.initial = '';

  if (
    warrantCase == null ||
    afccc == null ||
    afDetailsType == null ||
    afId == null ||
    currentStateMode == null ||
    selectedAfs == null
  ) {

    return clonedTemplate;
  }

  updateTargetInfoBaseOnTargetType(clonedTemplate, warrantCase, targetId as string)
  const afCCCInfo = getAfCCCInfo(afccc, afDetailsType);

  if (afCCCInfo == null) {
    return clonedTemplate;
  }

  const cccOptions = afCCCInfo.afSettings.find((setting) => setting.afids[0] === afId)?.options
    .reduce((total, option) => ({
      ...total,
      [option.key]: option.value,
    }), {}) as {[key: string]: string | number | boolean | string[]} | undefined;

  if (cccOptions == null) {
    return clonedTemplate;
  }

  Object.keys((clonedTemplate.general as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.general as FormTemplateSection).fields[key] != null) {
      if (cccOptions?.[key] != null) {
        (clonedTemplate.general as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      }
      (clonedTemplate.general as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  (clonedTemplate.targetInformation as FormTemplateSection).fields.LIID.initial = cccOptions.LIID;
  (clonedTemplate.targetInformation as FormTemplateSection).fields.DIR.initial = cccOptions.DIR ?
    cccOptions.DIR: (clonedTemplate.targetInformation as FormTemplateSection).fields.DIR.initial;
  (clonedTemplate.targetInformation as FormTemplateSection).fields.TON.initial = cccOptions.TON ?
    cccOptions.TON: (clonedTemplate.targetInformation as FormTemplateSection).fields.TON.initial;
  switch (cccOptions.MCFLAGS) {
    case '0x2000':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.connectedLinePresentation.initial = true;
      break;
    case '0x0080':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.separateCallContentChannel.initial = true;
      break;
    case '0x0100':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.suppressInterceptNumber.initial = true;
      break;
    case '0x2080':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.connectedLinePresentation.initial = true;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.separateCallContentChannel.initial = true;
      break;
    case '0x2100':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.connectedLinePresentation.initial = true;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.suppressInterceptNumber.initial = true;
      break;
    case '0x0180':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.separateCallContentChannel.initial = true;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.suppressInterceptNumber.initial = true;
      break;
    case '0x2180':
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.connectedLinePresentation.initial = true;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.separateCallContentChannel.initial = true;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.suppressInterceptNumber.initial = true;
      break;
    default:
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.connectedLinePresentation.initial = false;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.separateCallContentChannel.initial = false;
      (clonedTemplate.monitoringCenterFlags as FormTemplateSection).fields.suppressInterceptNumber.initial = false;
      break;
  }

  Object.keys((clonedTemplate.ICD as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.ICD as FormTemplateSection).fields[key] != null) {
      (clonedTemplate.ICD as FormTemplateSection).fields[key].initial = cccOptions?.[key];
      (clonedTemplate.ICD as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  const nettypeParts = (cccOptions?.NETTYPE as string)?.split('_');
  Object.keys((clonedTemplate.networkType as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.networkType as FormTemplateSection).fields[key] != null) {
      if (nettypeParts?.includes(key)) {
        (clonedTemplate.networkType as FormTemplateSection).fields[key].initial = true;
      }
      (clonedTemplate.networkType as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  const callContentParts = (cccOptions?.DATATYPE as string)?.split('_');
  Object.keys((clonedTemplate.callContentType as FormTemplateSection).fields).forEach((key: string) => {
    if ((clonedTemplate.callContentType as FormTemplateSection).fields[key] != null) {
      if (callContentParts?.includes(key)) {
        (clonedTemplate.callContentType as FormTemplateSection).fields[key].initial = true;
      }
      (clonedTemplate.callContentType as FormTemplateSection).fields[key].readOnly =
        currentStateMode === CONFIG_FORM_MODE.View;
    }
  });

  updateTemplateFieldsValues(clonedTemplate, warrantCase, targetId as string);
  clonedTemplate.formId = Date.now().toString();
  return clonedTemplate;
}

function enableField(
  field: FormTemplateField,
  shouldEnable: boolean,
):void {

  if (shouldEnable) {
    field.disabled = false;
  }
}

function disableField(
  field: FormTemplateField,
):void {
  field.disabled = true;
  field.initial = false;
}

function updateTemplateFieldsValues(template: FormTemplate, warrantCase : CaseModel, targetId : string) {
  const targetType = warrantCase.targets.find((target) => target.id === targetId)?.actualPrimaryType;
  updateNetworkTypeBaseOnTargetType(template, targetType);

  const netWorkType: FormTemplateSection = template.networkType as FormTemplateSection;
  if (netWorkType?.fields.GSM?.initial
    || netWorkType?.fields.FIX?.initial
    || netWorkType?.fields.POC?.initial
    || netWorkType?.fields.NGN?.initial
    || netWorkType?.fields.IMS?.initial
    || netWorkType?.fields.HLR?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.Voice, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.Voice);
  }

  enableField((template.callContentType as FormTemplateSection).fields.DATA, true);

  if (netWorkType?.fields.GSM?.initial
    || netWorkType?.fields.LTE?.initial
    || netWorkType?.fields.NGN?.initial
    || netWorkType?.fields.IMS?.initial
    || netWorkType?.fields.VOIP?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.MM, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.MM);
  }

  if (netWorkType?.fields.GPRS?.initial
    || netWorkType?.fields.LTE?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.IRIPO, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.IRIPO);
  }

  if (netWorkType?.fields.GSM?.initial
    || netWorkType?.fields.VOIP?.initial
    || netWorkType?.fields.NGN?.initial
    || netWorkType?.fields.IMS?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.IRIMM, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.IRIMM);
  }

  if (netWorkType?.fields.IA?.initial) {
    enableField((template.callContentType as FormTemplateSection).fields.IA, true);
  } else {
    disableField((template.callContentType as FormTemplateSection).fields.IA);
  }
}

function getLeaOptions(accessFunction: AccessFunction){
  let leaOptions: FormTemplateOption[] = [];
  if (accessFunction) {
    const leas: string[] = (accessFunction.dataInterfaces ?? [])
      .flatMap((df) => df.options)
      .filter((o) => ['lea', 'leacs', 'leaps'].includes(o.key))
      .map((o) => o.value as string);

    leaOptions = [
      ...leas.map((lea) => {
        return {
          label: lea,
          value: lea
        };
      })
    ];
  }
  return leaOptions;
}

function updateNetworkTypeBaseOnTargetType(template: FormTemplate, targetType: string | undefined) {
  if (targetType === 'MSISDN' || targetType === 'IMSI' || targetType === 'IMEI' || targetType === 'SIP-URI') {
    disableField((template.networkType as FormTemplateSection).fields.IA);
    disableField((template.networkType as FormTemplateSection).fields.FIX);
  }

  if (targetType === 'SIP-URI' || targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.VOIP);
  }

  if (targetType === 'IMSI' || targetType === 'IMEI') {
    disableField((template.networkType as FormTemplateSection).fields.NGN);
  }

  if (targetType === 'IMEI' || targetType === 'TEL-URI' || targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.HLR);
  }

  if (targetType === 'IMEI' || targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.POC);
  }

  if (targetType === 'SIP-URI' || targetType === 'TEL-URI') {
    disableField((template.networkType as FormTemplateSection).fields.GSM);
    disableField((template.networkType as FormTemplateSection).fields.GPRS);
  }

  if (targetType === 'PSTN') {
    disableField((template.networkType as FormTemplateSection).fields.IA);
    disableField((template.networkType as FormTemplateSection).fields.GPRS);
    disableField((template.networkType as FormTemplateSection).fields.LTE);
  }
}

function updateTargetInfoBaseOnTargetType(template: FormTemplate, warrantCase : CaseModel, targetId : string) {
  const targetType = warrantCase.targets.find((target) => target.id === targetId)?.primaryType;
  const targetInfoSection = (template.targetInformation as FormTemplateSection)
  if (targetType === 'PSTN') {
    targetInfoSection.fields.DIR.hide = 'false';
    targetInfoSection.fields.TON.hide = 'false';
  } else {
    targetInfoSection.fields.DIR.hide = 'true';
    targetInfoSection.fields.TON.hide = 'true';
  }
}

const UtimacoLIMSTargetCCC: FunctionComponent<UtimacoTargetCCCProps> = ({
  afDetails,
  handleUpdate,
  afid,
  currentStateMode,
  targetId,
  selectedAfs,
  warrantCase,
  selectedTargetAf,
  deliveryFunctionId,
  accessFunction,
  targetTemplates,
  getAccessFunctionWithViewOptions,
}: UtimacoTargetCCCProps) => {
  const [template, setTemplate] = useState<FormTemplate>(utimacoTemplate);
  const targetInfoRef = useRef<Form>(null);
  const monitoringCenterFlagsRef = useRef<Form>(null);
  const ICDRef = useRef<Form>(null);
  const networkTypeRef = useRef<Form>(null);
  const callContentTypeRef = useRef<Form>(null);
  const monitorCenterTypeRef = useRef<Form>(null);
  const generalRef = useRef<Form>(null);
  const { t } = useTranslation();

  const targetAccessFunctionId = selectedTargetAf.id;

  useEffect(() => {
    if (targetAccessFunctionId) {
      getAccessFunctionWithViewOptions(deliveryFunctionId, targetAccessFunctionId);
    }
  }, [targetAccessFunctionId, deliveryFunctionId, getAccessFunctionWithViewOptions]);

  useEffect(() => {
    const clonedTemplate = cloneDeep(template);

    const templateOptions = getTemplateOptions(targetTemplates || [], afDetails.type || '');

    (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.options = [
      { value: '', label: 'Select a template' },
      ...templateOptions,
    ];
    (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.initial = '';

    clonedTemplate.key = Date.now().toString();
    setTemplate(clonedTemplate);
  }, [selectedTargetAf, targetTemplates]);

  function fieldUpdate({ name, value } : ChangedField) {
    const clonedTemplate = cloneDeep(template);

    let targetInfoFields : TargetInfoUtimaco = targetInfoRef?.current?.getValues();
    let monitoringCenterFlagsFields : MCFlags = monitoringCenterFlagsRef?.current?.getValues();
    let ICDFields : ICDUtimaco = ICDRef?.current?.getValues();
    let networkTypeFields : NetworkTypeUtimaco = networkTypeRef?.current?.getValues();
    let callContentTypeFields : DataTypeUtimaco = callContentTypeRef?.current?.getValues();

    if (name === 'targetDeliveryTemplate') {
      // eslint-disable-next-line eqeqeq
      const clonedTargetTemplate = cloneDeep(targetTemplates.find((targetTemplate) => targetTemplate.id == value));
      targetInfoFields = getTargetTemplateValues('targetInformation', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);

      monitoringCenterFlagsFields = getTargetTemplateValues('monitoringCenterFlags', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);

      ICDFields = getTargetTemplateValues('ICD', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);
      networkTypeFields = getTargetTemplateValues('networkType', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);
      callContentTypeFields = getTargetTemplateValues('callContentType', clonedTargetTemplate as TargetDeliveryTemplateAccessFunction);

      (clonedTemplate.general as FormTemplateSection).fields.targetDeliveryTemplate.initial = value;
    }

    const generalFields: GeneralUtimaco = generalRef?.current?.getValues();

    const isValid = generalRef?.current?.isValid(true, true)
      && targetInfoRef?.current?.isValid(true, true)
      && monitoringCenterFlagsRef?.current?.isValid(true, true)
      && ICDRef?.current?.isValid(true, true)
      && networkTypeRef?.current?.isValid(true, true)
      && callContentTypeRef?.current?.isValid(true, true)
      && monitorCenterTypeRef?.current?.isValid(true, true);

    let options = prepareOptions(
      { ...targetInfoFields },
      { ...monitoringCenterFlagsFields },
      { ...ICDFields },
      { ...networkTypeFields },
      { ...callContentTypeFields }
    );

    options = [...options, { key: 'targetDeliveryTemplate', value: generalFields.targetDeliveryTemplate }];
    const afCCCSettings: TargetModelAfCCCSettings[] = [{
      afids: [afDetails.id as string],
      isValid,
      options,
      autowired: false,
    }];

    handleUpdate(afCCCSettings);
    clonedTemplate.key = Date.now().toString();
    setTemplate(clonedTemplate);
  }

  return (
    <Fragment>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).general as FormTemplateSection).fields
        }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        ).general as FormTemplateSection).metadata?.layout?.rows}
        ref={generalRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      <div className="subsection-header">
        {t('warrant.target.form.genband.targetInformation')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).targetInformation as FormTemplateSection).fields
      }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.targetInformation as FormTemplateSection).metadata?.layout?.rows}
        ref={targetInfoRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      <div className="subsection-header">
        {t('warrant.target.form.aramco.monitoringCenterFlags')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).monitoringCenterFlags as FormTemplateSection).fields
      }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.monitoringCenterFlags as FormTemplateSection).metadata?.layout?.rows}
        ref={monitoringCenterFlagsRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      <div className="subsection-header">
        {t('warrant.target.form.aramco.ICD')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).ICD as FormTemplateSection).fields
      }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.ICD as FormTemplateSection).metadata?.layout?.rows}
        ref={ICDRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      <div className="subsection-header">
        {t('warrant.target.form.aramco.networkType')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).networkType as FormTemplateSection).fields
      }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.networkType as FormTemplateSection).metadata?.layout?.rows}
        ref={networkTypeRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
      <div className="subsection-header">
        {t('warrant.target.form.aramco.callContentType')}
      </div>
      <Form
        name={template.key}
        fields={
          (adaptTargetCCCForm(
            warrantCase,
            accessFunction,
            targetTemplates,
            selectedAfs?.afs?.afccc,
            afDetails.type,
            afid,
            currentStateMode,
            selectedAfs,
            afid,
            targetId
          ).callContentType as FormTemplateSection).fields
      }
        layout={(adaptTargetCCCForm(
          warrantCase,
          accessFunction,
          targetTemplates,
          selectedAfs?.afs?.afccc,
          afDetails.type,
          afid,
          currentStateMode,
          selectedAfs,
          afid,
          targetId
        )?.callContentType as FormTemplateSection).metadata?.layout?.rows}
        ref={callContentTypeRef}
        flexFlow="row"
        fieldUpdateHandler={fieldUpdate}
        i18nKeyPrefix="warrant.target.form"
      />
    </Fragment>
  );
};

export default connector(UtimacoLIMSTargetCCC);
